import * as z from 'zod';
import { VolumeMode } from '../../database/schema.js';
import { calculateVolumeMl, calculateWeightGrams, convertDose, isActivityUnit, isMassUnit, sanitizeUnit, } from './calculations.js';
export const CompoundingMode = z.enum(['BOLUS', 'IN_PREFILLED', 'AD_PREFILLED', 'AD_EMPTY']);
const ContainerContents = z.enum(['EMPTY', 'FILLED']);
// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------
/**
 * LIQUID: strengthValue is a concentration (unit/mL), e.g. 10 mg/mL.
 * SOLID:  strengthValue is the total amount in the vial, e.g. 500 mg.
 *         reconstitutionSolventVolumeMl: manufacturer-specified inject volume.
 *         reconstitutedSolutionConcentration (preferred): concentration after dissolution (unit/mL).
 *         volumeExpansionMl (fallback): volume change after dissolution; totalVolumeMl = solventVolume + volumeExpansion.
 *
 * Reconstitution is driven by `reconstitutionRequired` (derived from the medicine form),
 * not by the physical form alone — a dry substance may not require reconstitution.
 *
 * Common fields:
 *   doseUnit: unit for the prescribed dose (e.g. mg), used for unit conversion.
 *   densityGcm3: substance density in g/cm³, used for weight calculation.
 */
const TradeNameSchema = z
    .discriminatedUnion('form', [
    z.object({
        form: z.literal('LIQUID'),
        strengthValue: z.number().gt(0),
        strengthUnit: z.string().nonempty().transform(sanitizeUnit),
        name: z.string().nonempty(),
        doseUnit: z.string().nonempty().transform(sanitizeUnit),
        densityGcm3: z.number().gt(0),
        reconstitutionRequired: z.boolean().optional(),
    }),
    z.object({
        form: z.literal('SOLID'),
        strengthValue: z.number().gt(0),
        strengthUnit: z.string().nonempty().transform(sanitizeUnit),
        name: z.string(),
        doseUnit: z.string().nonempty().transform(sanitizeUnit),
        densityGcm3: z.number().gt(0),
        reconstitutionRequired: z.boolean().optional(),
        /** Legacy single-solvent field kept for back-compat; prefer `reconstitutionSolvents`. */
        reconstitutionSolventVolumeMl: z.number().gt(0).optional(),
        /** A trade name may use several solvents; every row participates and the total volume is their sum. */
        reconstitutionSolvents: z
            .array(z.object({
            /** Solvent display name, used by the cleanroom step instructions. */
            name: z.string().nonempty(),
            volumeMl: z.number().gt(0),
        }))
            .min(1)
            .optional(),
        reconstitutedSolutionConcentration: z.number().gt(0).optional(),
        /** Unit of reconstitutedSolutionConcentration, e.g. mg/mL. Defaults to strengthUnit per mL. */
        reconstitutedConcentrationUnit: z.string().nonempty().transform(sanitizeUnit).optional(),
        volumeExpansionMl: z.number().optional(),
    }),
])
    .superRefine((data, ctx) => {
    const label = data.name || 'Trade name';
    const doseIsMass = isMassUnit(data.doseUnit);
    const doseIsActivity = isActivityUnit(data.doseUnit);
    const strengthIsMass = isMassUnit(data.strengthUnit);
    const strengthIsActivity = isActivityUnit(data.strengthUnit);
    // IU is biological activity, not mass: the dose and strength must describe the same quantity
    if ((doseIsMass && strengthIsActivity) || (doseIsActivity && strengthIsMass)) {
        ctx.addIssue({
            code: 'custom',
            message: `${label}: the dose unit ${data.doseUnit} does not match the strength unit ${data.strengthUnit}. Use IU for both or a mass unit for both.`,
            path: ['doseUnit'],
        });
    }
    if (data.form === 'SOLID') {
        // a reconstituted powder can derive its concentration from the vial strength and solvent
        if (!data.reconstitutionRequired && data.reconstitutedSolutionConcentration == null) {
            ctx.addIssue({
                code: 'custom',
                message: `${label}: set the reconstituted concentration (e.g. 2500 IU/mL) so the drawn volume can be calculated.`,
                path: ['reconstitutedSolutionConcentration'],
            });
        }
        if (data.reconstitutedSolutionConcentration != null && data.reconstitutedConcentrationUnit) {
            const [numerator = ''] = data.reconstitutedConcentrationUnit.split('/');
            const numeratorIsMass = isMassUnit(numerator);
            const numeratorIsActivity = isActivityUnit(numerator);
            if ((numeratorIsMass && strengthIsActivity) || (numeratorIsActivity && strengthIsMass)) {
                ctx.addIssue({
                    code: 'custom',
                    message: `${label}: the concentration unit ${data.reconstitutedConcentrationUnit} does not match the strength unit ${data.strengthUnit}. Use a concentration in the strength unit, e.g. IU/mL.`,
                    path: ['reconstitutedConcentrationUnit'],
                });
            }
        }
    }
    if (!data.reconstitutionRequired)
        return;
    if (data.form !== 'SOLID') {
        ctx.addIssue({
            code: 'custom',
            message: 'Reconstitution is only supported for SOLID trade names.',
            path: ['reconstitutionRequired'],
        });
        return;
    }
    if (data.reconstitutionSolventVolumeMl == null &&
        (data.reconstitutionSolvents == null || data.reconstitutionSolvents.length === 0)) {
        ctx.addIssue({
            code: 'custom',
            message: 'Reconstitution requires a solvent volume.',
            path: ['reconstitutionSolventVolumeMl'],
        });
    }
});
/** Substance-level prescription: only the fields that vary per substance. */
const PrescriptionSchema = z.object({
    prescribedDose: z.number().gt(0),
});
const CarrierSchema = z.object({
    /** the physical bag the picking list provides; may be larger than the prescribed volume */
    nominalVolumeMl: z.number().gt(0),
    /** the volume the preparation must end at; defaults to the bag volume (legacy payloads) */
    prescribedVolumeMl: z.number().gt(0).optional(),
    densityGcm3: z.number().gt(0),
});
/**
 * Preparation-level context: shared across all substances in the same order.
 * Drives mode derivation and carrier handling.
 * carrier is co-located with carrierRequired: true, making incoherent states unrepresentable.
 */
const PreparationSchema = z.discriminatedUnion('carrierRequired', [
    z.object({
        carrierRequired: z.literal(false),
    }),
    z.object({
        carrierRequired: z.literal(true),
        carrier: CarrierSchema,
        volumeMode: VolumeMode.nonoptional(),
        containerContents: ContainerContents.nullable(),
        /**
         * The carrier (and part of the drug) is already provided by a returned product.
         * The technician only tops up the missing drug. Treats the prep like a pre-filled
         * bag (IN_PREFILLED) regardless of volumeMode/containerContents — add-only, no withdrawal.
         */
        providedByReturn: z.boolean().optional(),
    }),
]);
/** A single substance within a multi-substance preparation. */
const SubstanceSchema = z.object({
    prescription: PrescriptionSchema,
    tradeName: TradeNameSchema,
    /** Fresh vials this dose is drawn from; drives the solvent drawn for a multi-vial reconstitution. */
    reconstitutionVialCount: z.int().gt(0).optional(),
});
const round = (num) => Number(num.toFixed(3));
/**
 * Splits the picked bag volume into the volume the preparation must end with
 * and the surplus carrier the operator withdraws first. Legacy payloads without
 * a prescribed volume keep the old behaviour: the bag is exact.
 */
function getCarrierVolumes(carrier) {
    const targetVolumeMl = carrier.prescribedVolumeMl ?? carrier.nominalVolumeMl;
    if (targetVolumeMl > carrier.nominalVolumeMl) {
        throw new Error('The prescribed volume exceeds the carrier bag volume.');
    }
    return { targetVolumeMl, excessVolumeMl: carrier.nominalVolumeMl - targetVolumeMl };
}
// ---------------------------------------------------------------------------
// Per-substance calculation
// ---------------------------------------------------------------------------
const VOLUME_UNIT_TO_ML = { mL: 1, dL: 100, L: 1000 };
/** % w/v is grams per 100 ml, so every percent is 10 mg/ml. */
const PERCENT_TO_MG_PER_ML = 10;
/**
 * The stored concentration is per reconstitutedConcentrationUnit (e.g. 20 mg/mL),
 * while the engine divides dose by a per-mL value in the strength unit. Convert
 * the numerator to strengthUnit and the denominator to mL. A percent concentration
 * is a mass concentration (% w/v = g/100 ml); units that cannot be converted throw,
 * because falling back to strengthUnit/mL silently drew ten times the volume (CY-355).
 */
function concentrationPerMlInStrengthUnit(concentration, concentrationUnit, strengthUnit) {
    if (!concentrationUnit)
        return concentration;
    const [numerator = '', denominator = 'mL'] = concentrationUnit.split('/');
    const denominatorMl = VOLUME_UNIT_TO_ML[denominator];
    if (numerator === '%') {
        if (denominatorMl !== 1 || !isMassUnit(strengthUnit)) {
            throw new Error(`Cannot use the concentration unit ${concentrationUnit} with the strength unit ${strengthUnit}: percent is a mass concentration per 100 ml.`);
        }
        return convertDose({ dose: concentration * PERCENT_TO_MG_PER_ML, from: 'mg', to: strengthUnit });
    }
    const numeratorIsMass = isMassUnit(numerator);
    const numeratorIsActivity = isActivityUnit(numerator);
    if (denominatorMl == null || (!numeratorIsMass && !numeratorIsActivity)) {
        throw new Error(`Cannot use the concentration unit ${concentrationUnit}: use a mass or activity unit per volume, e.g. mg/mL or IU/mL.`);
    }
    const strengthIsMass = isMassUnit(strengthUnit);
    const strengthIsActivity = isActivityUnit(strengthUnit);
    if (numeratorIsMass !== strengthIsMass || numeratorIsActivity !== strengthIsActivity) {
        throw new Error(`Cannot use the concentration unit ${concentrationUnit} with the strength unit ${strengthUnit}: the units measure different quantities.`);
    }
    return (convertDose({
        dose: concentration,
        from: numerator,
        to: strengthUnit,
    }) / denominatorMl);
}
function calculateBaseDrug(substance, carrier) {
    const { prescription, tradeName: tn } = substance;
    const normalizedDose = convertDose({
        dose: prescription.prescribedDose,
        from: tn.doseUnit,
        to: tn.strengthUnit,
    });
    let concentrationPerMl;
    let reconstitution;
    if (tn.reconstitutionRequired) {
        if (tn.form !== 'SOLID')
            throw new Error('Only SOLID trade names can require reconstitution.');
        if (tn.reconstitutionSolventVolumeMl == null && !tn.reconstitutionSolvents?.length)
            throw new Error('Reconstitution requires a solvent volume.');
        // without a carrier there is no density to borrow; use water (1 g/ml)
        const solventDensityGcm3 = carrier?.densityGcm3 ?? 1;
        // every solvent row participates; weights use the carrier density as a proxy
        // for the solvent density (differences between aqueous solvents are negligible)
        const solventRows = tn.reconstitutionSolvents?.length
            ? tn.reconstitutionSolvents
            : [{ name: 'solvent', volumeMl: tn.reconstitutionSolventVolumeMl }];
        const totalSolventVolumeMl = solventRows.reduce((acc, s) => acc + s.volumeMl, 0);
        // Hybrid approach to determine concentration after reconstitution:
        // 1. reconstitutedSolutionConcentration (preferred): manufacturer-specified concentration (e.g. 5 mg/mL).
        //    This is the most accurate value as it accounts for powder displacement and dissolution dynamics.
        // 2. volumeExpansionMl (fallback): volume change after adding solvent; totalDrawableVolume = solventVolume + volumeExpansion.
        //    When volumeExpansion is 0, drawable volume equals solvent volume (negligible displacement).
        // 3. If neither is set, throw an error — we cannot compute concentration without this information.
        if (tn.reconstitutedSolutionConcentration != null) {
            concentrationPerMl = concentrationPerMlInStrengthUnit(tn.reconstitutedSolutionConcentration, tn.reconstitutedConcentrationUnit, tn.strengthUnit);
        }
        else if (tn.volumeExpansionMl != null) {
            const totalVolumeMl = totalSolventVolumeMl + tn.volumeExpansionMl;
            concentrationPerMl = tn.strengthValue / totalVolumeMl;
        }
        else {
            throw new Error('SOLID trade requires either reconstitutedSolutionConcentration or volumeExpansionMl.');
        }
        // every solvent row participates; weights use the carrier density as a proxy
        // for the solvent density (differences between aqueous solvents are negligible)
        const vialCount = Math.max(1, substance.reconstitutionVialCount ?? 1);
        reconstitution = {
            required: true,
            solventVolumeMl: totalSolventVolumeMl,
            solventWeightGrams: calculateWeightGrams(totalSolventVolumeMl, solventDensityGcm3),
            solventDrawVolumeMl: round(totalSolventVolumeMl * vialCount),
            solventDrawWeightGrams: round(calculateWeightGrams(totalSolventVolumeMl * vialCount, solventDensityGcm3)),
            vials: vialCount,
            solvents: solventRows.map(solvent => ({
                name: solvent.name,
                volumeMl: solvent.volumeMl,
                weightGrams: calculateWeightGrams(solvent.volumeMl, solventDensityGcm3),
                drawVolumeMl: round(solvent.volumeMl * vialCount),
                drawWeightGrams: round(calculateWeightGrams(solvent.volumeMl * vialCount, solventDensityGcm3)),
            })),
            concentrationAfterDissolutionPerMl: round(concentrationPerMl),
        };
    }
    else {
        if (tn.form === 'SOLID') {
            if (tn.reconstitutedSolutionConcentration == null)
                throw new Error('A SOLID trade name that does not require reconstitution must specify reconstitutedSolutionConcentration.');
            concentrationPerMl = concentrationPerMlInStrengthUnit(tn.reconstitutedSolutionConcentration, tn.reconstitutedConcentrationUnit, tn.strengthUnit);
        }
        else {
            concentrationPerMl = tn.strengthValue;
        }
        reconstitution = { required: false };
    }
    const volumeMl = calculateVolumeMl(normalizedDose, concentrationPerMl);
    return {
        drugVolumeMl: round(volumeMl),
        targetDrugWeightGrams: round(calculateWeightGrams(volumeMl, tn.densityGcm3)),
        reconstitution,
        name: substance.tradeName.name,
    };
}
// ---------------------------------------------------------------------------
// Workflows
// ---------------------------------------------------------------------------
const workflows = {
    // UC1 / UC5: no carrier bag involved.
    // If carrier is provided, it defines the AD target volume and triggers a TOPUP.
    BOLUS: (substances, carrier) => {
        const results = substances.map(subst => calculateBaseDrug(subst, carrier));
        const totalVolumeMl = results.reduce((sum, r) => sum + r.drugVolumeMl, 0);
        const totalWeight = results.reduce((sum, r) => sum + r.targetDrugWeightGrams, 0);
        if (carrier !== null) {
            const targetVolumeMl = carrier.prescribedVolumeMl ?? carrier.nominalVolumeMl;
            if (totalVolumeMl > targetVolumeMl)
                throw new Error('Total drug volume exceeds target syringe capacity.');
            const topupVolumeMl = targetVolumeMl - totalVolumeMl;
            const topupWeightGrams = calculateWeightGrams(topupVolumeMl, carrier.densityGcm3);
            return {
                mode: 'BOLUS',
                substances: results,
                carrierVolumeMl: round(targetVolumeMl),
                carrierBagVolumeMl: round(carrier.nominalVolumeMl),
                finalVolumeMl: round(targetVolumeMl),
                carrierAction: { type: 'TOPUP', volumeMl: round(topupVolumeMl), weightGrams: round(topupWeightGrams) },
                expectedFinalDrugWeightGrams: round(totalWeight + topupWeightGrams),
                expectedFinalDrugVolumeMl: round(totalVolumeMl),
            };
        }
        return {
            mode: 'BOLUS',
            substances: results,
            carrierVolumeMl: null,
            finalVolumeMl: null,
            carrierAction: { type: 'NONE' },
            expectedFinalDrugWeightGrams: round(totalWeight),
            expectedFinalDrugVolumeMl: round(totalVolumeMl),
        };
    },
    // UC2: drug injected directly into a full pre-filled bag. Final volume increases.
    // When the picked bag is larger than prescribed, the surplus carrier is withdrawn first
    // (a negative reading on the tared bag) so the bag ends at the prescribed volume + drug.
    // expectedFinalDrugWeightGrams: baseline + total drug weight.
    IN_PREFILLED: (substances, carrier) => {
        const results = substances.map(subst => calculateBaseDrug(subst, carrier));
        const totalVolumeMl = results.reduce((sum, r) => sum + r.drugVolumeMl, 0);
        const totalWeight = results.reduce((sum, r) => sum + r.targetDrugWeightGrams, 0);
        const { targetVolumeMl, excessVolumeMl } = getCarrierVolumes(carrier);
        return {
            mode: 'IN_PREFILLED',
            substances: results,
            carrierVolumeMl: round(targetVolumeMl),
            carrierBagVolumeMl: round(carrier.nominalVolumeMl),
            finalVolumeMl: round(targetVolumeMl + totalVolumeMl),
            carrierAction: excessVolumeMl > 0
                ? {
                    type: 'WITHDRAW',
                    volumeMl: round(excessVolumeMl),
                    weightGrams: round(-calculateWeightGrams(excessVolumeMl, carrier.densityGcm3)),
                }
                : { type: 'NONE' },
            expectedFinalDrugWeightGrams: round(totalWeight),
            expectedFinalDrugVolumeMl: round(totalVolumeMl),
        };
    },
    // UC3: fixed final volume. Bag is tared first, then operator withdraws drug-equivalent
    // volume, then injects the drug. All scale checks are relative to the tared zero.
    // A larger picked bag adds its surplus to the withdrawal, so the bag ends at the
    // prescribed volume after the drug goes back in.
    // WITHDRAW.weightGrams is negative: expected reading after removing fluid from a tared scale.
    // expectedFinalDrugWeightGrams = totalWeight (carrier contribution is zero after tare).
    AD_PREFILLED: (substances, carrier) => {
        const results = substances.map(subst => calculateBaseDrug(subst, carrier));
        const totalVolumeMl = results.reduce((sum, r) => sum + r.drugVolumeMl, 0);
        const totalWeight = results.reduce((sum, r) => sum + r.targetDrugWeightGrams, 0);
        const { targetVolumeMl, excessVolumeMl } = getCarrierVolumes(carrier);
        if (totalVolumeMl > targetVolumeMl)
            throw new Error('Total drug volume exceeds pre-filled bag volume.');
        const withdrawVolumeMl = excessVolumeMl + totalVolumeMl;
        const withdrawWeightGrams = calculateWeightGrams(withdrawVolumeMl, carrier.densityGcm3);
        return {
            mode: 'AD_PREFILLED',
            substances: results,
            carrierVolumeMl: round(targetVolumeMl),
            carrierBagVolumeMl: round(carrier.nominalVolumeMl),
            finalVolumeMl: round(targetVolumeMl),
            carrierAction: { type: 'WITHDRAW', volumeMl: round(withdrawVolumeMl), weightGrams: round(-withdrawWeightGrams) },
            expectedFinalDrugWeightGrams: round(totalWeight),
            expectedFinalDrugVolumeMl: round(totalVolumeMl),
        };
    },
    // UC4: empty container (pump). Pharmacist fills the carrier themselves and tares the
    // filled container. The machine only validates the drug weight against the tared scale.
    // No ADD_BEFORE modelled: Sven confirmed the pharmacist handles priming independently.
    AD_EMPTY: (substances, carrier) => {
        const results = substances.map(subst => calculateBaseDrug(subst, carrier));
        const totalVolumeMl = results.reduce((sum, r) => sum + r.drugVolumeMl, 0);
        const totalWeight = results.reduce((sum, r) => sum + r.targetDrugWeightGrams, 0);
        const targetVolumeMl = carrier.prescribedVolumeMl ?? carrier.nominalVolumeMl;
        if (totalVolumeMl > targetVolumeMl)
            throw new Error('Total drug volume exceeds target container capacity.');
        // the pump must end at the nominal volume: only prime the carrier that the drug does not displace
        const primeVolumeMl = targetVolumeMl - totalVolumeMl;
        return {
            mode: 'AD_EMPTY',
            substances: results,
            carrierVolumeMl: round(targetVolumeMl),
            carrierBagVolumeMl: round(carrier.nominalVolumeMl),
            finalVolumeMl: round(targetVolumeMl),
            carrierAction: {
                type: 'PRIME',
                volumeMl: round(primeVolumeMl),
                weightGrams: round(calculateWeightGrams(primeVolumeMl, carrier.densityGcm3)),
            },
            expectedFinalDrugWeightGrams: round(totalWeight),
            expectedFinalDrugVolumeMl: round(totalVolumeMl),
        };
    },
};
// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------
export const CalculateCompoundingTargetsSchema = z.object({
    preparation: PreparationSchema,
    substances: z.array(SubstanceSchema).min(1),
});
function deriveMode(preparation) {
    if (!preparation.carrierRequired)
        return 'BOLUS';
    // A returned product already provides the carrier (and part of the drug).
    // The technician only tops up the missing drug — treated as a pre-filled bag.
    if (preparation.providedByReturn)
        return 'IN_PREFILLED';
    const { volumeMode, containerContents } = preparation;
    if (volumeMode === VolumeMode.enum.IN)
        return 'IN_PREFILLED';
    switch (containerContents) {
        case ContainerContents.enum.FILLED:
            return 'AD_PREFILLED';
        case ContainerContents.enum.EMPTY:
            return 'AD_EMPTY';
        default:
            throw new Error('AD carrier requires containerContents.');
    }
}
export function calculateCompoundingTargets(payload) {
    const { preparation, substances } = CalculateCompoundingTargetsSchema.parse(payload);
    const mode = deriveMode(preparation);
    const carrier = preparation.carrierRequired ? preparation.carrier : null;
    if (mode === 'BOLUS')
        return workflows.BOLUS(substances, carrier);
    const { carrier: requiredCarrier } = preparation;
    switch (mode) {
        case 'IN_PREFILLED':
            return workflows.IN_PREFILLED(substances, requiredCarrier);
        case 'AD_PREFILLED':
            return workflows.AD_PREFILLED(substances, requiredCarrier);
        case 'AD_EMPTY':
            return workflows.AD_EMPTY(substances, requiredCarrier);
    }
}
// ---------------------------------------------------------------------------
// Smoke tests
// ---------------------------------------------------------------------------
export const CompundingTestPayloads = {
    /**
     * UC1 — Fluorouracil 900 mg bolus
     */
    bolusSingle: {
        preparation: { carrierRequired: false },
        substances: [
            {
                prescription: { prescribedDose: 900 },
                tradeName: {
                    form: 'LIQUID',
                    strengthValue: 50,
                    strengthUnit: 'mg',
                    name: 'Fluorouracil',
                    doseUnit: 'mg',
                    densityGcm3: 1.53,
                },
            },
        ],
    },
    /**
     * UC2 — Rituximab 650 mg in 500 ml NaCl
     */
    inPrefilledSingle: {
        preparation: {
            carrierRequired: true,
            volumeMode: 'IN',
            containerContents: 'FILLED',
            carrier: { nominalVolumeMl: 500, densityGcm3: 1.0046 },
        },
        substances: [
            {
                prescription: { prescribedDose: 650 },
                tradeName: {
                    form: 'LIQUID',
                    strengthValue: 10,
                    strengthUnit: 'mg',
                    name: 'Rituximab',
                    doseUnit: 'mg',
                    densityGcm3: 1.012,
                },
            },
        ],
    },
    /**
     * UC2-multi — Rituximab 650 mg + Methylprednisolone 100 mg in 500 ml NaCl
     */
    inPrefilledMultiple: {
        preparation: {
            carrierRequired: true,
            volumeMode: 'IN',
            containerContents: 'FILLED',
            carrier: { nominalVolumeMl: 500, densityGcm3: 1.0046 },
        },
        substances: [
            {
                prescription: { prescribedDose: 650 },
                tradeName: {
                    form: 'LIQUID',
                    strengthValue: 10,
                    strengthUnit: 'mg',
                    name: 'Rituximab',
                    doseUnit: 'mg',
                    densityGcm3: 1.012,
                },
            },
            {
                prescription: { prescribedDose: 100 },
                tradeName: {
                    form: 'LIQUID',
                    strengthValue: 10,
                    strengthUnit: 'mg',
                    name: 'Methylprednisolone',
                    doseUnit: 'mg',
                    densityGcm3: 1.008,
                },
            },
        ],
    },
    /**
     * UC3 — Rituximab 650 mg ad 500 ml NaCl (bag tared, withdraw then inject)
     */
    adPrefilledSingle: {
        preparation: {
            carrierRequired: true,
            volumeMode: 'AD',
            containerContents: 'FILLED',
            carrier: { nominalVolumeMl: 500, densityGcm3: 1.0046 },
        },
        substances: [
            {
                prescription: { prescribedDose: 650 },
                tradeName: {
                    form: 'LIQUID',
                    strengthValue: 10,
                    strengthUnit: 'mg',
                    name: 'Rituximab',
                    doseUnit: 'mg',
                    densityGcm3: 1.011,
                },
            },
        ],
    },
    /**
     * UC3-multi — Rituximab 650 mg + Methylprednisolone 100 mg ad 500 ml NaCl
     */
    adPrefilledMultiple: {
        preparation: {
            carrierRequired: true,
            volumeMode: 'AD',
            containerContents: 'FILLED',
            carrier: { nominalVolumeMl: 500, densityGcm3: 1.0046 },
        },
        substances: [
            {
                prescription: { prescribedDose: 650 },
                tradeName: {
                    form: 'LIQUID',
                    strengthValue: 10,
                    strengthUnit: 'mg',
                    name: 'Rituximab',
                    doseUnit: 'mg',
                    densityGcm3: 1.011,
                },
            },
            {
                prescription: { prescribedDose: 100 },
                tradeName: {
                    form: 'LIQUID',
                    strengthValue: 10,
                    strengthUnit: 'mg',
                    name: 'Methylprednisolone',
                    doseUnit: 'mg',
                    densityGcm3: 1.008,
                },
            },
        ],
    },
    /**
     * UC4 — Fluorouracil 3750 mg ad 240 ml pump (pharmacist primes, machine validates drug only)
     */
    adEmpty: {
        preparation: {
            carrierRequired: true,
            volumeMode: 'AD',
            containerContents: 'EMPTY',
            carrier: { nominalVolumeMl: 240, densityGcm3: 1.0046 },
        },
        substances: [
            {
                prescription: { prescribedDose: 3750 },
                tradeName: {
                    form: 'LIQUID',
                    strengthValue: 25,
                    strengthUnit: 'mg',
                    name: 'Fluorouracil',
                    doseUnit: 'mg',
                    densityGcm3: 1.53,
                },
            },
        ],
    },
    /**
     * Solid with a g strength and an mg/mL reconstituted concentration.
     */
    solidConcentrationUnits: {
        preparation: {
            carrierRequired: true,
            volumeMode: 'IN',
            containerContents: 'FILLED',
            carrier: { nominalVolumeMl: 500, densityGcm3: 1.0046 },
        },
        substances: [
            {
                prescription: { prescribedDose: 2 },
                tradeName: {
                    form: 'SOLID',
                    strengthValue: 1,
                    strengthUnit: 'g',
                    name: 'Cyclophosphamide',
                    doseUnit: 'mg',
                    densityGcm3: 1,
                    reconstitutionRequired: false,
                    reconstitutedSolutionConcentration: 20,
                    reconstitutedConcentrationUnit: 'mg/mL',
                },
            },
        ],
    },
    /**
     * UC5-multi — two powder vials reconstituted before compounding
     */
    inPrefilledMultipleSolid: {
        preparation: {
            carrierRequired: true,
            volumeMode: 'IN',
            containerContents: 'FILLED',
            carrier: { nominalVolumeMl: 500, densityGcm3: 1.0046 },
        },
        substances: [
            {
                prescription: { prescribedDose: 2.6 },
                tradeName: {
                    form: 'SOLID',
                    strengthValue: 2.6,
                    strengthUnit: 'mg',
                    name: 'Bortezomib rest vial',
                    doseUnit: 'mg',
                    densityGcm3: 1.012,
                    reconstitutionRequired: true,
                    reconstitutionSolventVolumeMl: 1.4,
                    reconstitutedSolutionConcentration: 2.5,
                },
            },
            {
                prescription: { prescribedDose: 2.6 },
                tradeName: {
                    form: 'SOLID',
                    strengthValue: 2.6,
                    strengthUnit: 'mg',
                    name: 'Bortezomib new vial',
                    doseUnit: 'mg',
                    densityGcm3: 1.012,
                    reconstitutionRequired: true,
                    reconstitutionSolventVolumeMl: 1.4,
                    reconstitutedSolutionConcentration: 2.5,
                },
            },
        ],
    },
    /**
     * Solid powder without a carrier (s.c. / plain bolus).
     */
    bolusSolidNoCarrier: {
        preparation: { carrierRequired: false },
        substances: [
            {
                prescription: { prescribedDose: 2.6 },
                tradeName: {
                    form: 'SOLID',
                    strengthValue: 2.6,
                    strengthUnit: 'mg',
                    name: 'Bortezomib',
                    doseUnit: 'mg',
                    densityGcm3: 1.012,
                    reconstitutionRequired: true,
                    reconstitutionSolventVolumeMl: 1.4,
                    reconstitutedSolutionConcentration: 2.5,
                },
            },
        ],
    },
    /**
     * UC5 — Bortezomib 2.6 mg ad 10 ml bolus (solid powder, reconstitute then top up)
     */
    bolusSolid: {
        preparation: {
            carrierRequired: true,
            volumeMode: 'AD',
            containerContents: 'EMPTY',
            carrier: { nominalVolumeMl: 10, densityGcm3: 1.0046 },
        },
        substances: [
            {
                prescription: { prescribedDose: 2.6 },
                tradeName: {
                    form: 'SOLID',
                    strengthValue: 2.6,
                    strengthUnit: 'mg',
                    doseUnit: 'mg',
                    densityGcm3: 1.012,
                    reconstitutionRequired: true,
                    reconstitutionSolventVolumeMl: 1.4,
                    reconstitutedSolutionConcentration: 2.5,
                    name: 'Bortezomib',
                },
            },
        ],
    },
};
// function runTests() {
//   try {
//     console.log({
//       label: 'UC1 – BOLUS single substance',
//       result: calculateCompoundingTargets(CompundingTestPayloads.bolusSingle),
//     })
//     console.log({
//       label: 'UC2 – IN_PREFILLED single substance',
//       result: calculateCompoundingTargets(CompundingTestPayloads.inPrefilledSingle),
//     })
//     console.log({
//       label: 'UC2-multi – IN_PREFILLED two substances',
//       result: calculateCompoundingTargets(CompundingTestPayloads.inPrefilledMultiple),
//     })
//     console.log({
//       label: 'UC3 – AD_PREFILLED single substance',
//       result: calculateCompoundingTargets(CompundingTestPayloads.adPrefilledSingle),
//     })
//     console.log({
//       label: 'UC3-multi – AD_PREFILLED two substances',
//       result: calculateCompoundingTargets(CompundingTestPayloads.adPrefilledMultiple),
//     })
//     console.log({
//       label: 'UC4 – AD_EMPTY pump',
//       result: calculateCompoundingTargets(CompundingTestPayloads.adEmpty),
//     })
//     console.log({
//       label: 'UC5 – BOLUS solid single substance',
//       result: calculateCompoundingTargets(CompundingTestPayloads.bolusSolid),
//     })
//   } catch (error) {
//     console.error(error)
//   }
// }
// runTests()
//# sourceMappingURL=compounding.js.map